import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Grid,
  Rating,
  CircularProgress,
  Chip,
  Divider,
  Paper,
  alpha,
  useTheme,
} from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import ReplyIcon from '@mui/icons-material/Reply';
import { getGoogleBusinessResenasStore } from '../../../../store/ratings/thunks';

const starMapping: { [key: string]: number } = {
  ONE: 1,
  TWO: 2,
  THREE: 3,
  FOUR: 4,
  FIVE: 5,
};

export const Resenas = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const [reviews, setReviews] = useState<any[]>([]);

  useEffect(() => {
    const fetchResenas = async () => {
      const response = await dispatch(getGoogleBusinessResenasStore());
      if (response && response.ok) {
        setReviews(response.data || []);
      }
    };
    fetchResenas();
  }, [dispatch]);

  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    };
    return new Date(dateString).toLocaleDateString('es-ES', options);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      {/* HEADER */}
      <Paper
        elevation={0}
        sx={{
          p: 4,
          mb: 4,
          borderRadius: 4,
          background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{ position: 'relative', zIndex: 2, display: 'flex', alignItems: 'center', gap: 2 }}
        >
          <Avatar
            sx={{ bgcolor: 'white', color: theme.palette.primary.main, width: 56, height: 56 }}
          >
            <GoogleIcon fontSize="large" />
          </Avatar>
          <Box>
            <Typography variant="h4" fontWeight="800" gutterBottom sx={{ mb: 0 }}>
              Reseñas de Google
            </Typography>
            <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
              Monitorea y gestiona los comentarios de tus clientes en tiempo real
            </Typography>
          </Box>
        </Box>
        <GoogleIcon
          sx={{
            position: 'absolute',
            right: -20,
            bottom: -20,
            fontSize: 200,
            opacity: 0.1,
            transform: 'rotate(-15deg)',
          }}
        />
      </Paper>

      {/* REVIEWS GRID */}
      {reviews.length === 0 ? (
        <Box textAlign="center" py={10}>
          <Typography variant="h6" color="text.secondary">
            No se encontraron reseñas por el momento.
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {reviews.map((review, index) => {
            const numericRating = starMapping[review.starRating] || 0;
            const isPositive = numericRating >= 4;

            return (
              <Grid size={{ xs: 12, md: 6, lg: 4 }} key={review.reviewId || index}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: `0 12px 24px ${alpha(theme.palette.primary.main, 0.1)}`,
                      borderColor: theme.palette.primary.light,
                    },
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, p: 3 }}>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="flex-start"
                      mb={2}
                    >
                      <Box display="flex" gap={2} alignItems="center">
                        <Avatar
                          src={review.reviewer?.profilePhotoUrl}
                          alt={review.reviewer?.displayName}
                          sx={{ width: 48, height: 48, border: '2px solid white', boxShadow: 1 }}
                        >
                          {review.reviewer?.displayName?.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle1" fontWeight="bold" lineHeight={1.2}>
                            {review.reviewer?.displayName || 'Usuario Anónimo'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(review.createTime)}
                          </Typography>
                        </Box>
                      </Box>
                      <GoogleIcon sx={{ color: '#EA4335', fontSize: 20 }} />
                    </Box>

                    <Box display="flex" alignItems="center" gap={1} mb={2}>
                      <Rating
                        value={numericRating}
                        readOnly
                        size="small"
                        sx={{
                          color: '#FBBC04',
                        }}
                      />
                      <Chip
                        label={`${numericRating}.0`}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.75rem',
                          fontWeight: 'bold',
                          bgcolor: isPositive ? alpha('#34A853', 0.1) : alpha('#EA4335', 0.1),
                          color: isPositive ? '#34A853' : '#EA4335',
                        }}
                      />
                    </Box>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 4,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        lineHeight: 1.6,
                        minHeight: '6.4em',
                        fontStyle: !review.comment ? 'italic' : 'normal',
                      }}
                    >
                      {review.comment
                        ? `"${review.comment}"`
                        : 'El usuario no dejó ningún comentario, solo la calificación.'}
                    </Typography>

                    {review.reviewReply && (
                      <Box
                        sx={{
                          p: 2,
                          bgcolor: alpha(theme.palette.primary.main, 0.04),
                          borderRadius: 2,
                          borderLeft: `3px solid ${theme.palette.primary.main}`,
                        }}
                      >
                        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                          <ReplyIcon fontSize="small" color="primary" />
                          <Typography variant="caption" fontWeight="bold" color="primary">
                            Respuesta del propietario
                          </Typography>
                        </Box>
                        <Typography
                          variant="body2"
                          color="text.primary"
                          sx={{ fontSize: '0.8rem' }}
                        >
                          {review.reviewReply.comment}
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );
};

export default Resenas;
